import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Sparkles } from "lucide-react";
import api from "@/lib/api";
import BrandMark from "@/components/BrandMark";
import { IDS } from "@/constants/testIds";

const OCCASIONS = [
  "Wedding", "Reception", "Sangeet", "Mehendi", "Haldi", "Pooja",
  "Diwali party", "Festival", "Party", "Date", "Interview",
  "Office event", "Trip",
];

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-2">
    <span className="text-[10px] font-[Manrope] font-bold tracking-[0.2em] uppercase text-stone-500">{label}</span>
    {children}
  </label>
);

export default function EventNew() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    date: "",
    occasion: OCCASIONS[0],
    with_whom: "",
    dress_code: "",
  });

  const create = async () => {
    if (!form.name.trim()) return toast.error("Give your event a name");
    if (!form.date) return toast.error("Pick a date");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        date: form.date,
        occasion: form.occasion,
        with_whom: form.with_whom.trim() || undefined,
        dress_code: form.dress_code.trim() || undefined,
      };
      const { data } = await api.post("/events", payload);
      toast.success("Lookbook ready");
      navigate(`/events/${data.event_id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FBF9F6] grain" style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom, 0.75rem))" }}>
      <div className="max-w-md mx-auto px-6 pt-12">
        <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-2 text-sm font-[Manrope] text-stone-600">
          <ChevronLeft size={16} /> back
        </button>
        <BrandMark className="mb-4" />
        <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-[#6B7A54] mb-2">
          New event
        </div>
        <h1 className="font-[Cormorant_Garamond] text-4xl leading-tight tracking-tight text-stone-900 mb-8">
          Plan the look.
        </h1>

        <div className="flex flex-col gap-5">
          <Field label="Event name">
            <input
              data-testid={IDS.eventName}
              className="input"
              placeholder="e.g., Priya's sangeet"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field label="Date">
            <input
              data-testid={IDS.eventDate}
              className="input"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </Field>
          <Field label="Occasion">
            <div className="flex flex-wrap gap-2" data-testid={IDS.eventOccasion}>
              {OCCASIONS.map((o) => (
                <button
                  key={o}
                  onClick={() => setForm({ ...form, occasion: o })}
                  className={`px-4 py-2 rounded-full border text-sm font-[Manrope] transition-colors ${
                    form.occasion === o
                      ? "bg-[#6B7A54] text-white border-transparent"
                      : "bg-white border-stone-200 text-stone-700"
                  }`}
                >
                  {o}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Who are you meeting? (optional)">
            <input
              data-testid={IDS.eventWithWhom}
              className="input"
              placeholder="e.g., Priya, in-laws, college friends"
              value={form.with_whom}
              onChange={(e) => setForm({ ...form, with_whom: e.target.value })}
            />
          </Field>
          <Field label="Dress code (optional)">
            <input
              data-testid={IDS.eventDressCode}
              className="input"
              placeholder="e.g., pastel, black tie, ethnic"
              value={form.dress_code}
              onChange={(e) => setForm({ ...form, dress_code: e.target.value })}
            />
          </Field>

          <button
            data-testid={IDS.eventCreateBtn}
            onClick={create}
            disabled={saving}
            className="btn-primary mt-4"
          >
            <Sparkles size={16} strokeWidth={1.5} />
            {saving ? "Composing lookbook…" : "Create lookbook"}
          </button>
          <p className="text-xs font-[Manrope] text-stone-500 text-center">
            I&rsquo;ll draft 3–4 complete looks. This takes a few seconds.
          </p>
        </div>
      </div>
    </div>
  );
}
